import {
	useState,
	useEffect,
	useRef,
	type ChangeEvent
} from "react";
import {
	Box,
	IconButton,
	Chip,
	Switch,
	CircularProgress,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Divider,
	TextField,
	InputAdornment,
	Checkbox,
	FormControlLabel,
} from '@mui/material';
import { getProfileCommonJss } from './ProfileCommonJss'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import constant from '../../../constants/constant'
import Check from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LocationService from '../../../services/LocationService'
import AddRoundedIcon from "@mui/icons-material/AddRounded";

type ClientClass = {
	id: number
	name?: string
	code?: string
	parent_class_id?: number | null
	deleted_at?: string | null
	service_type_id?: number | string
	_type?: string
	location_links?: Array<Record<string, any>>
	[key: string]: any
}

type LocationClassLink = {
	clientclass_id: number
	locationclassid: number
	[key: string]: any
}

type LocationItem = {
	id: number
	client_id: number
	class_links: LocationClassLink[]
	[key: string]: any
}

type FilterState = {
	otb: boolean
	pnotb: boolean
	notb: boolean
	dis: boolean
	history: boolean
	deleted: boolean
}

type ClientProfileClassListProps = {
	classes?: ClientClass[]
	currentLocations?: LocationItem[]
	onClassChange: (items: ClientClass[]) => void
	onCurrentLocationChange?: (location: LocationItem) => void
	multiMode: boolean
	createClass: (item: ClientClass) => void
	assignAvailable?: boolean
	onMultiModeChange?: false | ((event: ChangeEvent<HTMLInputElement>) => void)
	isAdmin?: boolean
	visualVariant?: string
}

function ClientProfileClassList(props: ClientProfileClassListProps) {
	const {
		classes, 
		currentLocations, 
		onClassChange, 
		onCurrentLocationChange, 
		multiMode,
		createClass,
		assignAvailable=false,
		onMultiModeChange=false,
		isAdmin = false,
	} = props
	const jss = getProfileCommonJss(props?.visualVariant)
	const [existClasses, setExistClasses] = useState<number[]>([])
	const [choosedClasses, setChoosedClasses] = useState<ClientClass[]>([])
	const [keyword, setKeyword] = useState('')
	const [showAllCheck, setShowAllCheck] = useState(false)
	const [filter, setFilter] = useState<FilterState>({
		otb: false,
		pnotb: false,
		notb: false,
		dis: false,
		history: false,
		deleted: false
	})
	const [openFilter, setOpenFilter] = useState(false)
	const filterElement = useRef<HTMLButtonElement | null>(null)

	useEffect(() => {
		if( currentLocations && currentLocations.length>0 ) {
			let _exists: number[] = []
			currentLocations.forEach(i => {
				i.class_links.forEach(_link => {
					if( _exists.indexOf(_link.clientclass_id)==-1 ) {
						_exists.push(_link.clientclass_id)
					}
				})
			})
			setExistClasses(_exists)
			setChoosedClasses([])
		}
	}, [currentLocations])

	useEffect(() => {
		if( choosedClasses.length>0 ) {
			onClassChange(choosedClasses)	
		}
	}, [choosedClasses, onClassChange])

	useEffect(() => {
		setChoosedClasses([])
	}, [multiMode])

	function chooseClass(clientClass: ClientClass) {
		if( multiMode ) {
			let _choosedClasses = choosedClasses.map(i => {return i})
			if( choosedClasses.indexOf(clientClass)>-1 ) {
				_choosedClasses.splice(choosedClasses.indexOf(clientClass), 1)
			} else {
				_choosedClasses.push(clientClass)
			}
			setChoosedClasses(_choosedClasses)
		} else {
			setChoosedClasses([clientClass])
		}
	}

	function changeFilter(type: keyof FilterState | 'all') {
		if( type == 'all' ) {
			setFilter({...filter, otb:false, pnotb:false, notb:false, history: false, dis: false})
		} else {
			setFilter({...filter, [type]: !filter[type]})
		}
	}

	function updateKeyword(e: ChangeEvent<HTMLInputElement>) {
		setKeyword(e.target.value)
	}

	function swtichAllCheck() {
		setShowAllCheck(!showAllCheck)
	}

	async function changeClassLocationLink(clientClass: ClientClass) {
		try {
			if( !currentLocations || currentLocations.length===0 ) {
				return
			}

			const copyCurrentLocation = {...currentLocations[0]}
			if( existClasses.indexOf(clientClass.id)==-1 ) {
				const _link = await LocationService.linkLocationClass(currentLocations[0].client_id, currentLocations[0].id, clientClass.id)
				copyCurrentLocation.class_links.push(_link)
			} else {
				const _link = await LocationService.unlinkLocationClass(currentLocations[0].client_id, currentLocations[0].id, clientClass.id)
				let origin_class_links = currentLocations[0].class_links.filter((item: LocationClassLink) => {
					return item.locationclassid!=_link.locationclassid
				})
				copyCurrentLocation.class_links = origin_class_links
			}
			if( onCurrentLocationChange ) {
				onCurrentLocationChange(copyCurrentLocation)	
			}
		} catch(e) {
		}
	}

	function addClass() {
		let name = 'New Class'
		createClass({name: name, id: -1, code: '', location_links: []})
	}

	let filtered_classes: ClientClass[] = []
	if( classes ) {
		const normalizedKeyword = keyword?.trim().toLowerCase()
		const serviceTypeId = {
			otb: String(constant.SERVICE_TYPE_ID.OTB),
			pnotb: String(constant.SERVICE_TYPE_ID.PNOTB),
			notb: String(constant.SERVICE_TYPE_ID.NOTB),
			history: String(constant.SERVICE_TYPE_ID.History),
			dis: String(constant.SERVICE_TYPE_ID.Distress),
			warehouse: String(constant.SERVICE_TYPE_ID.Warehouse),
			child: String(constant.SERVICE_TYPE_ID.Child),
		}
		filtered_classes = classes.filter(item => {
			if( normalizedKeyword ) {
				const name = item.name?.toLowerCase() || ''
				const code = item.code?.toLowerCase() || ''
				if( name.indexOf(normalizedKeyword)==-1 && code.indexOf(normalizedKeyword)==-1 ) {
					return false
				}
			}
			if( filter.deleted ) {
				return true
			} else {
				return !item.deleted_at
			}
		}).sort((a, b) => {
			if( !normalizedKeyword ) {
				return 0
			}

			const aCode = (a.code || '').toLowerCase()
			const bCode = (b.code || '').toLowerCase()
			const aName = (a.name || '').toLowerCase()
			const bName = (b.name || '').toLowerCase()

			const aCodeMatch = aCode.indexOf(normalizedKeyword) > -1
			const bCodeMatch = bCode.indexOf(normalizedKeyword) > -1
			if( aCodeMatch !== bCodeMatch ) {
				return aCodeMatch ? -1 : 1
			}

			const aNameMatch = aName.indexOf(normalizedKeyword) > -1
			const bNameMatch = bName.indexOf(normalizedKeyword) > -1
			if( aNameMatch !== bNameMatch ) {
				return aNameMatch ? -1 : 1
			}

			return 0
		}).filter(item => {
			let result = false
			const currentTypeId = String(item.service_type_id ?? '')
			if( !filter.otb && !filter.pnotb && !filter.notb && !filter.dis && !filter.history ) {
				result = true
			} else {
				if( filter.otb && currentTypeId===serviceTypeId.otb ) {
					result = true
				}
				if( filter.pnotb && currentTypeId===serviceTypeId.pnotb ) {
					result = true
				}
				if( filter.notb && currentTypeId===serviceTypeId.notb ) {
					result = true
				}
				if( filter.history && currentTypeId===serviceTypeId.history ) {
					result = true
				}
				if( filter.dis && currentTypeId===serviceTypeId.dis ) {
					result = true
				}
			}
			return result
		})
		if( !showAllCheck && assignAvailable ) {
			filtered_classes = filtered_classes.filter(item => {
				return existClasses.indexOf(Number(item.id)) > -1
			})
		}
	}

	return <Box sx={jss.dataGridItem}>
		<Box sx={jss.dataTop}>
			<Box sx={jss.dataTitle}>
				Classes
				{onMultiModeChange && isAdmin && 
					<FormControlLabel 
						control={<Switch value={multiMode} size="small" onChange={onMultiModeChange}/>} 
						label="Multi select" 
						sx={{ml:2}}
					/>
				}
			</Box>
		</Box>
		<Box sx={jss.actionSection}>
			<TextField 
				size="small"
				variant="outlined"
				placeholder="Search"
				value={keyword}
				onChange={updateKeyword}
				InputProps={{
					startAdornment: (
					<InputAdornment position="start">
						<SearchIcon />
					</InputAdornment>
					),
				}}
			/>
			<Box sx={jss.dataAction}>
				<IconButton size="small" onClick={e => setOpenFilter(true)} ref={filterElement}><FilterListRoundedIcon /></IconButton>
				{!multiMode && assignAvailable && isAdmin ?
					<IconButton size="small" sx={showAllCheck ? {...jss.iconButtonActive} : {}} onClick={swtichAllCheck}><PlaylistAddCheckIcon /></IconButton>
				: null}
				<Menu open={openFilter} anchorEl={filterElement.current} onClose={e => {setOpenFilter(false)}}>
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('all')}>
						<ListItemIcon>{(!filter.otb && !filter.pnotb && !filter.notb && !filter.dis && !filter.history) && <Check />}</ListItemIcon>
						<ListItemText >Show All</ListItemText>
					</MenuItem>
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('otb')}>
						<ListItemIcon>{filter.otb && <Check />}</ListItemIcon>
						<ListItemText >OTB</ListItemText>
					</MenuItem>
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('pnotb')}>
						<ListItemIcon>{filter.pnotb && <Check />}</ListItemIcon>
						<ListItemText >PNOTB</ListItemText>
					</MenuItem>
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('notb')}>
						<ListItemIcon>{filter.notb && <Check />}</ListItemIcon>
						<ListItemText >NOTB</ListItemText>
					</MenuItem>
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('dis')}>
						<ListItemIcon>{filter.dis && <Check />}</ListItemIcon>
						<ListItemText >Distress</ListItemText>
					</MenuItem>
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('history')}>
						<ListItemIcon>{filter.history && <Check />}</ListItemIcon>
						<ListItemText >History</ListItemText>
					</MenuItem>
					<Divider />
					<MenuItem sx={{width:200}} dense onClick={e => changeFilter('deleted')}>
						<ListItemIcon>{filter.deleted && <Check />}</ListItemIcon>
						<ListItemText >Dropped</ListItemText>
					</MenuItem>
				</Menu>
				{isAdmin ? <IconButton size="small" onClick={e => addClass()}><AddRoundedIcon /></IconButton> : null}
			</Box>
		</Box>
		{!classes && 
			<Box sx={jss.loadingSection}>
				<CircularProgress />
			</Box>
		}
		{(classes && (!assignAvailable || (currentLocations && currentLocations.length>0))) && 
			<Box sx={jss.dataContent}>
				{filtered_classes.map(_class => {
					let _className = ""
					const classTypeId = String(_class.service_type_id ?? '')
					if( choosedClasses?.map(i=>{return i.id}).indexOf(_class.id)>-1 ) {
						_className = '_active'
					} 
					if( !multiMode ) {
						if( choosedClasses?.map(i=>{return i.parent_class_id}).indexOf(_class.id)>-1 ) {
							_className = '_parent_active'
						} else if( choosedClasses.length>0 && _class.parent_class_id!=null && choosedClasses?.map(i=>{return i.id}).indexOf(_class.parent_class_id)>-1 ) {
							_className = '_child_active'
						}
					}
					if( _class.deleted_at ) {
						_className += ' _deleted '
					}
					if( _class?._type == 'child' ) {
						_className += ' _child'
					}
					return <Box 
							sx={jss.dataContentItem} 
							className={_className} 
							key={`location_class_${_class.id}`} 
						>
						{showAllCheck &&
							<Checkbox 
								size="small" 
								checked={existClasses.indexOf(_class.id)>-1}
								onClick={e => changeClassLocationLink(_class)}
							/>
						}
						<Box sx={jss.dataContentItem.name} onClick={e => chooseClass(_class)}>
							{_class.name}
						</Box>
						<Box sx={jss.dataContentItem.action} onClick={e => chooseClass(_class)}>
							{_class.code}
							{classTypeId===String(constant.SERVICE_TYPE_ID.OTB) &&
								<Chip label="O" size="small"/>
							}
							{classTypeId===String(constant.SERVICE_TYPE_ID.PNOTB) &&
								<Chip label="P" size="small"/>
							}
							{classTypeId===String(constant.SERVICE_TYPE_ID.NOTB) &&
								<Chip label="N" size="small"/>
							}
							{classTypeId===String(constant.SERVICE_TYPE_ID.History) &&
								<Chip label="H" size="small"/>
							}
							{classTypeId===String(constant.SERVICE_TYPE_ID.Distress) &&
								<Chip label="D" size="small"/>
							}
							{classTypeId===String(constant.SERVICE_TYPE_ID.Warehouse) &&
								<Chip label="W" size="small"/>
							}
							{classTypeId===String(constant.SERVICE_TYPE_ID.Child) &&
								<Chip label="C" size="small"/>
							}
						</Box>
					</Box>	
				})}
				{assignAvailable && filtered_classes.length==0 &&
					<Box sx={jss.dataContent.empty}>
						<Box>There are no classes for the selected location.</Box>
						<Box>You can select classes for this location.</Box>
					</Box>
				}
			</Box>
		}
		
	</Box>
}
export default ClientProfileClassList